/**
 * Enrollment step rendered conditionally during the `complete-intake`
 * activity flow. Wraps the existing `EnrollmentSection` and projects the
 * user's selection back into the intake form's chain fields.
 *
 * @agentic connections
 */

import type { UseFormReturnType } from "@mantine/form";
import { EnrollmentSection } from "../sections/EnrollmentSection";
import type {
  ClinicalIntakeInteractionFormData,
  CoCMIntakeInteractionFormData,
} from "@benchmark/core/types";

/** Intake form data shapes that include the chain fields. @agentic connections */
type EnrollmentCapableFormData =
  ClinicalIntakeInteractionFormData | CoCMIntakeInteractionFormData;

/**
 * Props for {@link EnrollmentStep}.
 *
 * @agentic connections
 */
export type EnrollmentStepProps<T extends EnrollmentCapableFormData> = {
  form: UseFormReturnType<T>;
};

/**
 * Enrollment step that projects program-selection results into the form.
 *
 * @agentic connections
 */
export const EnrollmentStep = <T extends EnrollmentCapableFormData>({
  form,
}: EnrollmentStepProps<T>) => (
  <EnrollmentSection
    onSubmit={(data) => {
      if (!data) {
        form.setFieldValue("followup-enroll" as never, "" as never);
        form.setFieldValue(
          "followup-program-selection" as never,
          undefined as never,
        );
        form.setFieldValue(
          "followup-include-referrals" as never,
          false as never,
        );
        form.setFieldValue("followup-service-needs" as never, [] as never);
        form.setFieldValue(
          "followup-requires-approval" as never,
          true as never,
        );
        return;
      }

      if (data.enrollmentDecision === "enroll-later") {
        form.setFieldValue("followup-enroll" as never, "enroll-later" as never);
        form.setFieldValue(
          "followup-program-selection" as never,
          undefined as never,
        );
        form.setFieldValue(
          "followup-include-referrals" as never,
          false as never,
        );
        form.setFieldValue("followup-service-needs" as never, [] as never);
        form.setFieldValue(
          "followup-requires-approval" as never,
          true as never,
        );
        return;
      }

      form.setFieldValue("followup-enroll" as never, "enroll-now" as never);
      form.setFieldValue("followup-program-selection" as never, data as never);
      form.setFieldValue(
        "followup-include-referrals" as never,
        (data.includeReferrals === true) as never,
      );
      form.setFieldValue("followup-assigned-to" as never, "" as never);
      form.setFieldValue("followup-assigned-to-display" as never, "" as never);
    }}
    initialValues={
      (form.values["followup-enroll"] as string | undefined) === "enroll-later"
        ? { enrollmentDecision: "enroll-later" }
        : ((form.values["followup-program-selection" as keyof T] as Parameters<
            typeof EnrollmentSection
          >[0]["initialValues"]) ?? undefined)
    }
  />
);
